/**
 * ClipCard — pure card for one inbound clip (no platform APIs/store; `ClipDock` wires those).
 * Content is MASKED when `sensitive`/`forceMask` (public mode) until reveal-on-tap; the copy button is the ONLY OS-clipboard write, gated on explicit click.
 * SECURITY: clip `content` is decrypted plaintext — never logged.
 */

import { Check, Copy01, Eye, EyeOff } from "@untitledui/icons";
import { useCallback, useEffect, useRef, useState } from "react";
import { formatRelativeTime } from "../../utils/format";
import { IconButton } from "../IconButton";
import { Text } from "../Text";
import styles from "./ClipCard.module.css";

export interface ClipCardLabels {
  /** "from {label}" provenance prefix — passed the origin label. */
  from: (label: string) => string;
  /** aria-label for the copy button. */
  copy: string;
  /** aria-label for the copy button once copied (brief confirmation). */
  copied: string;
  /** aria-label for the reveal toggle when content is masked. */
  reveal: string;
  /** aria-label for the reveal toggle when content is shown. */
  hide: string;
  /** Visible chip text marking a sensitive clip. */
  sensitive: string;
  /** Cloud-provenance chip text ("from your API token"). */
  cloud: string;
  /** sr-only text announced while content is masked. Defaults to the English string. */
  maskedHint?: string;
}

export interface ClipCardProps {
  content: string;
  /** Origin device/surface display name. */
  originLabel: string;
  /** Capture timestamp (ms) — rendered as relative time. */
  timestamp: number;
  /** Sender-asserted sensitive hint — masks the preview until revealed. */
  sensitive?: boolean;
  /** Privacy ("public") mode — masks the preview regardless of the sender hint. */
  forceMask?: boolean;
  /** Trust tier; "cloud" surfaces a wary "from your API token" chip. */
  provenance: "device" | "cloud";
  /** Copy content to the OS clipboard (host-supplied `writeText`); invoked only on click, resolves to flash "copied". */
  onCopy: (content: string) => Promise<void>;
  labels: ClipCardLabels;
}

export function ClipCard({
  content,
  originLabel,
  timestamp,
  sensitive,
  forceMask,
  provenance,
  onCopy,
  labels,
}: ClipCardProps) {
  // A clip is masked when the sender flags it sensitive OR public mode
  // is on; either case renders the reveal toggle so the user can show
  // the content on demand. Non-masked clips show content directly.
  const [revealed, setRevealed] = useState(false);
  const [copied, setCopied] = useState(false);
  // True while onCopy() is awaiting; gates the Copy button so a fast
  // double-click can't fire two concurrent writes.
  const [copying, setCopying] = useState(false);
  // Holds the "copied" flash timer so we can cancel it if the card
  // unmounts within 1.5s of a copy (e.g. clearClips() on disconnect),
  // avoiding a setState on an unmounted component.
  const copiedTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  // Tracks mount state so the async copy `.then()` can't setState after
  // the card unmounts (e.g. clearClips() on disconnect races a copy).
  const mountedRef = useRef(true);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      clearTimeout(copiedTimer.current);
    };
  }, []);

  // Re-mask an already-revealed clip when public mode turns on, so toggling
  // privacy back on doesn't leave revealed plaintext visible.
  useEffect(() => {
    if (forceMask) setRevealed(false);
  }, [forceMask]);

  const maskable = sensitive === true || forceMask === true;
  const masked = maskable && !revealed;

  const handleCopy = useCallback(() => {
    // Fire-and-forget with a transient "copied" flash. The write itself
    // happens inside this click handler so it carries user activation.
    setCopying(true);
    void onCopy(content).then(
      () => {
        if (!mountedRef.current) return;
        setCopying(false);
        setCopied(true);
        clearTimeout(copiedTimer.current);
        copiedTimer.current = setTimeout(() => setCopied(false), 1500);
      },
      () => {
        // Swallow — the host logs the failure (never logs content). A
        // failed copy just doesn't flash the confirmation.
        if (mountedRef.current) setCopying(false);
      }
    );
  }, [content, onCopy]);

  return (
    <div className={styles.card}>
      <div className={styles.header}>
        <Text size="xs" tone="secondary" weight="medium">
          {labels.from(originLabel)}
        </Text>
        <Text size="xs" tone="tertiary">
          {formatRelativeTime(timestamp)}
        </Text>
      </div>

      {(sensitive || provenance === "cloud") && (
        <div className={styles.chips}>
          {sensitive && (
            <span className={styles.chip} data-tone="sensitive">
              {labels.sensitive}
            </span>
          )}
          {provenance === "cloud" && (
            <span className={styles.chip} data-tone="cloud">
              {labels.cloud}
            </span>
          )}
        </div>
      )}

      <div className={styles.body}>
        <pre
          className={styles.content}
          data-masked={masked || undefined}
          aria-hidden={masked || undefined}
          // Hover preview (no OS-clipboard write); capped at 120 chars so the full
          // decrypted payload never sits in a passive DOM attr. Omitted when masked.
          title={masked ? undefined : [...content].slice(0, 120).join("")}
        >
          {masked ? "••••••••••••" : content}
        </pre>
        {masked && (
          <span className={styles.srOnly}>
            {labels.maskedHint ?? "Content hidden — press Reveal to show"}
          </span>
        )}
        {/* Live region: announces the transient "Copied" confirmation to AT. */}
        <span className={styles.srOnly} role="status">
          {copied ? labels.copied : ""}
        </span>
        <div className={styles.actions}>
          {maskable && (
            <IconButton
              icon={revealed ? EyeOff : Eye}
              label={revealed ? labels.hide : labels.reveal}
              onClick={() => setRevealed((v) => !v)}
            />
          )}
          <IconButton
            icon={copied ? Check : Copy01}
            label={copied ? labels.copied : labels.copy}
            onClick={handleCopy}
            // Require reveal before copy so the user sees what they're copying (sensitive is a mask hint, not a security boundary); also gate while a write is in-flight (double-click guard).
            disabled={masked || copying}
          />
        </div>
      </div>
    </div>
  );
}
